import { dmyToYmd, ymdToDmy } from './reportDateParams'
import type { OutstandingBalanceListPatch } from '../types/utils'
import { OUTSTANDING_BALANCE_SESSION_KEY } from '../constants/sessionKeys'

export { OUTSTANDING_BALANCE_SESSION_KEY }

export function readOutstandingBalanceSession(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(OUTSTANDING_BALANCE_SESSION_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as unknown
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

export function writeOutstandingBalanceSession(data: Record<string, unknown>): void {
  sessionStorage.setItem(OUTSTANDING_BALANCE_SESSION_KEY, JSON.stringify(data))
}

export function clearOutstandingBalanceSession(): void {
  sessionStorage.removeItem(OUTSTANDING_BALANCE_SESSION_KEY)
}

function normalizeDate(value: unknown): string {
  if (value == null) return ''
  const s = String(value).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  return dmyToYmd(s) || s
}

export function sessionFilterToOutstandingBalancePatch(
  session: Record<string, unknown>,
): OutstandingBalanceListPatch {
  const patch: OutstandingBalanceListPatch = {}
  if (typeof session.page === 'number' && session.page > 0) patch.page = session.page
  if (typeof session.perPage === 'number' && session.perPage > 0) patch.perPage = session.perPage
  if (session.sortBy === 'asc' || session.sortBy === 'desc') patch.sortBy = session.sortBy
  if (typeof session.orderBy === 'string' && session.orderBy.trim()) patch.orderBy = session.orderBy
  if (typeof session.q === 'string') patch.q = session.q
  if (session.company_id != null && session.company_id !== '') patch.company_id = String(session.company_id)
  if (session.property_id != null && session.property_id !== '') patch.property_id = String(session.property_id)
  if (typeof session.application_status === 'string') patch.application_status = session.application_status
  if (typeof session.amount_condition === 'string') patch.amount_condition = session.amount_condition
  if (session.payment_amount != null && session.payment_amount !== '') {
    patch.payment_amount = String(session.payment_amount)
  }
  if (typeof session.chargeback_type === 'string') patch.chargeback_type = session.chargeback_type
  const fromDt = normalizeDate(session.fromDt)
  const toDt = normalizeDate(session.toDt)
  if (fromDt) patch.fromDt = fromDt
  if (toDt) patch.toDt = toDt
  return patch
}

export function buildOutstandingBalanceSessionFromParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  q: string
  company_id: string
  property_id: string
  application_status: string
  amount_condition: string
  payment_amount: string
  chargeback_type: string
  fromDt: string
  toDt: string
}): Record<string, unknown> {
  const out: Record<string, unknown> = {
    page: p.page,
    perPage: p.perPage,
    sortBy: p.sortBy,
    orderBy: p.orderBy,
    q: p.q,
    application_status: p.application_status,
    chargeback_type: p.chargeback_type,
  }
  if (p.company_id) out.company_id = p.company_id
  if (p.property_id) out.property_id = p.property_id
  if (p.amount_condition) out.amount_condition = p.amount_condition
  if (p.payment_amount) out.payment_amount = p.payment_amount
  const fromDt = ymdToDmy(p.fromDt)
  const toDt = ymdToDmy(p.toDt)
  if (fromDt) out.fromDt = fromDt
  if (toDt) out.toDt = toDt
  return out
}
